/**
 * Cliente da API. Os tipos aqui são o espelho do `CONTRATOS.md` — divergir
 * deles é o defeito mais caro do projeto, porque só aparece quando o coletor
 * real for ligado.
 */

export type EstadoFonte = 'no_ar' | 'indisponivel' | 'pendente'

export type Fonte = {
  id: string
  estado: EstadoFonte
  detalhe: string
  lidoEm: string | null
  ms: number | null
}

/**
 * ⚠️ `vendido: null` é DIFERENTE de `vendido: 0`.
 *
 * `null` significa "ninguém mediu isto" — fonte pendente, divisão ausente do
 * snapshot, banco daquela marca indisponível. A tela mostra `—`, **nunca
 * "R$ 0"**, e o `vendidoTotal` soma só o que foi medido.
 *
 * Zero é um número; a ausência de número não é zero. "Nunca mostrar zero por
 * falha" é requisito explícito do painel, e no telão de uma War Room um
 * "R$ 0,0 mi" ao lado de uma meta de R$ 3 mi é uma pergunta na sala.
 */
export type Subitem = {
  codigo: string
  rotulo: string
  meta: number
  vendido: number | null
  obs: string | null
}

export type Divisao = {
  id: string
  nome: string
  sigla: string
  ordem: number
  logo: string | null
  meta: number
  vendido: number | null
  subitens: Subitem[]
  /**
   * Os TRÊS primeiros do ranking desta divisão.
   *
   * Vêm junto na visão geral porque são a matéria do pódio da comemoração de
   * cada milhão: o telão mostra o top 3 das oito marcas de uma vez, e buscar
   * `/api/marca/:id` oito vezes no instante em que a tela explode em confete é
   * a hora errada para oito consultas.
   *
   * Lista VAZIA enquanto o ranking daquela divisão não foi medido — e aí a
   * marca não entra no pódio, em vez de aparecer sem campeão.
   *
   * **Também é atualizada pelo delta do SSE**, a partir do `representantes` que
   * ele já traz. Sem isso, uma página que carregou antes do primeiro ranking
   * ficava com a lista vazia para sempre — e foi exatamente o que esvaziou o
   * pódio na comemoração do sexto milhão, em 13/10: o painel havia reiniciado
   * no deploy minutos antes.
   */
  topRepresentantes: Array<{
    posicao: number
    nome: string | null
    fantasia: string | null
    nomePessoal: string | null
    valor: number
  }>
}

export type Pedido = {
  /**
   * Continua sendo a chave de idempotência da gravação, mesmo tendo saído da
   * tela: é por ele que receber o mesmo pedido duas vezes não duplica linha.
   */
  numero: string
  /**
   * O que a tela mostra no lugar do número: o **grupo econômico** quando o
   * cliente tem um, senão o **nome do cliente**. `null` quando a fonte não
   * souber — e aí a tela mostra `—`, como em todo lugar.
   */
  cliente: string | null
  valor: number
  dataPedido: string | null
  dataFaturamento: string | null
  subitem: string | null
  /**
   * `false` quando o pedido **não** entrou no `vendido` da divisão — ele foi
   * digitado na janela, mas fatura fora de 2026.
   *
   * A razão de existir: as duas janelas são diferentes de propósito (a métrica
   * tem teto de faturamento, o feed não), então **a lista cresce mais rápido que
   * o número**. Sem marcar isso na linha, alguém na sala vê pedido entrando e o
   * velocímetro parado e conclui que o painel travou.
   */
  contaNaMeta: boolean
}

export type Painel = {
  metaGeral: number
  vendidoTotal: number | null
  coletadoEm: string | null
  idadeSeg: number
  /**
   * `true` quando o número na tela é de demonstração (o coletor ainda não
   * mandou nada e `WR_DEMONSTRACAO=1`). Obriga o aviso visível — o risco de
   * alguém fotografar o telão e tratar o número como real é alto demais para
   * depender de quem configurou lembrar do que ligou.
   */
  demonstracao: boolean
  rotacao: { segGeral: number; segMarca: number; segPrefetch: number }
  fontes: Fonte[]
  divisoes: Divisao[]
}

/**
 * Uma linha do ranking de representante — o que o card mostra desde 13/10, no
 * lugar do feed de pedidos.
 *
 * Os TRÊS nomes vêm juntos porque a tela mostra os três, e cada um pode faltar
 * no cadastro do ERP: o `DIRETO DE FABRICA` não tem pessoa nem fantasia, e um
 * representante fora de `PEDI_020` chega com os três nulos — a linha vale
 * mesmo assim, porque carrega valor que **está** no total da divisão.
 */
export type Representante = {
  /** 1 é o primeiro lugar. Vem do servidor; é ela que decide a medalha. */
  posicao: number
  /**
   * Código do representante no ERP. Não aparece na tela: é a **chave da linha**
   * no React. Com a posição no lugar dele, uma troca de 3º lugar reusaria o nó
   * da linha anterior e a medalha trocaria de cor na pessoa errada.
   *
   * `null` em pedido cujo representante não está em `PEDI_020`.
   */
  codigo: string | null
  /** Razão social. */
  nome: string | null
  fantasia: string | null
  nomePessoal: string | null
  valor: number
  /**
   * Quantos pedidos somam esse valor. É informação de apoio, não o que ordena —
   * e superestima quando um pedido tem itens de dois catálogos (a contagem vem
   * somada por catálogo do ERP).
   */
  pedidos: number
}

export type VisaoMarca = {
  id: string
  vendido: number | null
  subitens: Subitem[]
  pedidos: Pedido[]
  /** Já ordenado e cortado em 15 pelo servidor: a tela não reordena. */
  representantes: Representante[]
  atualizadoEm: string | null
}

export type Usuario = { nome: string; papel: 'visualizador' | 'admin' | 'quiosque' }

/** O delta que o SSE empurra a cada snapshot aceito. */
export type Delta = {
  coletadoEm: string
  demonstracao?: boolean
  divisoes: Array<{
    id: string
    vendido: number | null
    subitens: Array<{ codigo: string; vendido: number | null }>
    pedidosNovos: Pedido[]
    /**
     * O ranking INTEIRO, e só quando mudou. Ausente é "não mudou" — e também é
     * o que chega no ciclo em que só a fonte sem ranking (o Linx) respondeu.
     * Nunca é delta: a ORDEM é o conteúdo, e uma troca de primeiro lugar mexe
     * em duas linhas de uma vez.
     */
    representantes?: Representante[]
  }>
  fontes?: Fonte[]
}

export class ErroApi extends Error {
  constructor(public status: number, mensagem: string) {
    super(mensagem)
  }
}

/**
 * `credentials: 'same-origin'` é o que manda o cookie de sessão. Sem isso toda
 * rota responde 401 e o sintoma parece ser de autenticação, não de fetch.
 */
async function pedir<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  /* `FormData` NÃO pode levar Content-Type definido por nós: o navegador tem de
     pôr o cabeçalho com o `boundary`, e defini-lo à mão quebra o multipart no
     servidor com um erro que aponta para o parser, não para cá. */
  const ehFormulario = typeof FormData !== 'undefined' && opcoes.body instanceof FormData
  const cabecalhos =
    opcoes.body && !ehFormulario ? { 'Content-Type': 'application/json' } : undefined

  const resposta = await fetch(caminho, {
    ...opcoes,
    credentials: 'same-origin',
    headers: { ...cabecalhos, ...opcoes.headers },
  })
  if (!resposta.ok) {
    let mensagem = `falha ${resposta.status}`
    try {
      const corpo = await resposta.json()
      if (corpo?.erro) mensagem = corpo.erro
    } catch {
      /* resposta sem JSON: a mensagem genérica basta */
    }
    throw new ErroApi(resposta.status, mensagem)
  }
  if (resposta.status === 204) return undefined as T
  return resposta.json() as Promise<T>
}

export const api = {
  sessao: () => pedir<{ usuario: Usuario | null }>('/api/sessao'),

  entrar: (nome: string, senha: string) =>
    pedir<{ usuario: Usuario }>('/api/login', {
      method: 'POST',
      body: JSON.stringify({ nome, senha }),
    }),

  sair: () => pedir<void>('/api/logout', { method: 'POST' }),

  painel: () => pedir<Painel>('/api/painel'),

  marca: (id: string) => pedir<VisaoMarca>(`/api/marca/${encodeURIComponent(id)}`),

  admin: {
    cadastro: () => pedir<{ divisoes: Divisao[]; metaGeral: number; configuracao: Record<string, string> }>(
      '/api/admin/cadastro',
    ),
    salvarDivisoes: (divisoes: unknown[]) =>
      pedir<{ ok: true }>('/api/admin/divisoes', { method: 'PUT', body: JSON.stringify({ divisoes }) }),
    salvarMetas: (metaGeral: number, metas: unknown[]) =>
      pedir<{ ok: true }>('/api/admin/metas', { method: 'PUT', body: JSON.stringify({ metaGeral, metas }) }),
    fontes: () => pedir<{ fontes: Fonte[]; rejeicoes: number }>('/api/admin/fontes'),
    /* Recarga manual da aba Fontes. `fonte: null` pede todas. `atendida: false`
       é resposta possível e não é erro: o painel não consulta o ERP, ele só
       encaminha o pedido ao coletor — e diz a verdade quando não há para quem. */
    recarregar: (fonte: string | null) =>
      pedir<{ ok: true; fonte: string | null; atendida: boolean; solicitadaEm: string }>(
        '/api/admin/recarregar',
        { method: 'POST', body: JSON.stringify({ fonte }) },
      ),
    /* Liga e desliga o filtro por data de faturamento (P-16). A resposta traz o
       estado GRAVADO, não o que foi pedido: é ele que a tela passa a mostrar, e
       assim um erro de gravação não deixa o interruptor mentindo. */
    definirFiltroFaturamento: (ligado: boolean, de?: string, ate?: string) =>
      pedir<{ filtroFaturamento: { ligado: boolean; de: string | null; ate: string | null } }>(
        '/api/admin/filtro-faturamento',
        { method: 'PUT', body: JSON.stringify({ ligado, de, ate }) },
      ),
    enviarLogo: (id: string, arquivo: File) => {
      const corpo = new FormData()
      corpo.append('logo', arquivo)
      return pedir<{ logo: string }>(`/api/admin/logo/${encodeURIComponent(id)}`, {
        method: 'POST',
        body: corpo,
      })
    },
  },
}
